import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import graphqlClient from '../../../lib/graphql-client';

const GET_PAGE_LAYOUTS = `
  query GetPageLayouts {
    pageLayouts {
      items {
        id
        pageId
        pageName
        layout
      }
    }
  }
`;

function renderBlock(block: any) {
  switch (block.type) {
    case 'hero':
      return (
        <div key={block.id} style={{ background: block.props.bg }} className="p-12 text-white text-center">
          <h1 className="text-4xl font-bold mb-2">{block.props.title}</h1>
          <p className="text-lg">{block.props.subtitle}</p>
        </div>
      );
    case 'text':
      return (
        <div key={block.id} className="p-8 max-w-2xl mx-auto">
          <p className="text-gray-700 text-lg">{block.props.content}</p>
        </div>
      );
    case 'image':
      return (
        <div key={block.id} className="p-8 max-w-2xl mx-auto">
          <img src={block.props.url} alt={block.props.alt} className="w-full rounded-lg shadow" />
        </div>
      );
    case 'button':
      return (
        <div key={block.id} className="p-4 text-center">
          <button style={{ background: block.props.color }} className="px-8 py-3 rounded-lg text-white font-medium text-lg shadow">
            {block.props.label}
          </button>
        </div>
      );
    default:
      return null;
  }
}

export default function PreviewPage() {
  const { pageId } = useParams<{ pageId?: string }>();
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState<any[]>([]);
  const [pageName, setPageName] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [debugInfo, setDebugInfo] = useState<{ urlPageId: string; items: any[]; error?: string } | null>(null);

  useEffect(() => {
    graphqlClient
      .query<any>({ query: GET_PAGE_LAYOUTS })
      .then(res => {
        const items: any[] = res?.pageLayouts?.items || [];
        setDebugInfo({ urlPageId: pageId ?? '(none)', items });
        const match = pageId ? items.find((p: any) => p.pageId === pageId || p.id === pageId) : null;
        if (!match) {
          setNotFound(true);
        } else {
          try {
            const parsed = JSON.parse(match.layout || '{}');
            setPageName(parsed.pageName || match.pageId);
            setBlocks(parsed.blocks || []);
          } catch {
            setNotFound(true);
          }
        }
      })
      .catch(() => {
        localStorage.setItem('preview-redirect', window.location.pathname);
        navigate('/login');
      })
      .finally(() => setLoading(false));
  }, [pageId, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-500">
        Loading...
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-center p-8">
        <p className="text-5xl mb-4">🔍</p>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Page not found</h1>
        <p className="text-gray-500 mb-6">No page matched this URL.</p>
        {debugInfo && (
          <div className="text-left bg-gray-100 rounded-lg p-4 max-w-xl w-full text-sm font-mono">
            <p className="font-bold mb-2 text-gray-700">Debug info:</p>
            <p><span className="text-gray-500">URL pageId:</span> {debugInfo.urlPageId}</p>
            {debugInfo.error && <p className="text-red-500 mt-1">Error: {debugInfo.error}</p>}
            <p className="mt-2 font-bold text-gray-700">Items from API ({debugInfo.items.length}):</p>
            {debugInfo.items.length === 0
              ? <p className="text-gray-400">— none —</p>
              : debugInfo.items.map((item, i) => (
                <div key={i} className="mt-1 border-t border-gray-200 pt-1">
                  <p><span className="text-gray-500">id:</span> {item.id}</p>
                  <p><span className="text-gray-500">pageId:</span> {item.pageId}</p>
                </div>
              ))
            }
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gray-900 text-white px-6 py-3 flex items-center gap-3">
        <span className="text-sm text-gray-400">🌐 Preview</span>
        <span className="font-semibold">{pageName}</span>
      </div>
      <div>{blocks.map(renderBlock)}</div>
    </div>
  );
}
